/**
 * DateRangeFilter — the SINGLE date-range control shared across
 * Sales Overview, Sales Explorer, and every other analytics page.
 *
 * Why this exists
 * ---------------
 * Previously Sales Overview and Sales Explorer each shipped their own
 * date filter with different defaults and different mental models
 * (Overview = preset pills + Custom popover; Explorer = "All time /
 * Month / Custom" select). Shared links did not reproduce the same
 * window. This component is the contract that ends that drift.
 *
 * Contract
 * --------
 *   value:      { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
 *   onChange:   (from, to) => void   // always called with valid ISO
 *   testIdPrefix: forwarded to internal `data-testid` attributes
 *
 * Behaviour
 * ---------
 *   • Presets render as a pill row + a Custom popover (calendar).
 *   • The active preset is reverse-derived from the applied range, so
 *     a deep-link to a manual range correctly highlights "Custom".
 *   • Default landing range (This Month) is enforced at the call-site
 *     via `resolveInitialRange()` — NOT inside this component, so the
 *     URL contract stays explicit at the page boundary.
 */
import React, { useEffect, useState } from 'react';
import { CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  RANGE_PRESETS, rangeFromPreset, presetFromRange,
  isValidISO, fmtDDMMYYYY,
} from '../lib/dateRange';
import { fmtLocalISO } from '../lib/date';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarUI } from './ui/calendar';

export default function DateRangeFilter({
  value,
  onChange,
  testIdPrefix = 'date-range',
  align = 'end',
}) {
  const dateFrom = value?.from || '';
  const dateTo   = value?.to   || '';
  const rangeKey = presetFromRange(dateFrom, dateTo);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(dateFrom ? new Date(dateFrom) : null);
  const [draftTo,   setDraftTo]   = useState(dateTo   ? new Date(dateTo)   : null);

  // Reset draft whenever the popover opens, so each session starts
  // from the *currently applied* range — never from a stale draft.
  useEffect(() => {
    if (popoverOpen) {
      setDraftFrom(dateFrom ? new Date(dateFrom) : null);
      setDraftTo(dateTo ? new Date(dateTo) : null);
    }
  }, [popoverOpen, dateFrom, dateTo]);

  const handlePreset = (key) => {
    if (key === 'custom') return; // popover handles Custom
    const r = rangeFromPreset(key);
    onChange(r.from, r.to);
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) {
      toast.error('Please pick both a start and end date');
      return;
    }
    let f = draftFrom, t = draftTo;
    if (f > t) [f, t] = [t, f];
    const fromIso = fmtLocalISO(f);
    const toIso   = fmtLocalISO(t);
    if (!isValidISO(fromIso) || !isValidISO(toIso)) {
      toast.error('Invalid dates'); return;
    }
    onChange(fromIso, toIso);
    setPopoverOpen(false);
  };

  return (
    <div
      className="flex bg-black/40 border border-white/10 rounded-lg overflow-hidden flex-wrap"
      data-testid={`${testIdPrefix}-presets`}
      data-active-range-key={rangeKey}
      data-date-from={dateFrom}
      data-date-to={dateTo}
    >
      {RANGE_PRESETS.filter((p) => p.key !== 'custom').map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => handlePreset(p.key)}
          data-testid={`${testIdPrefix}-${p.key}`}
          className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wide transition-all ${
            rangeKey === p.key
              ? 'bg-[#D4FF00]/15 text-[#D4FF00]'
              : 'text-zinc-400 hover:text-zinc-200'
          }`}
        >{p.label}</button>
      ))}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testIdPrefix}-custom`}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wide transition-all flex items-center gap-1 ${
              rangeKey === 'custom'
                ? 'bg-[#D4FF00]/15 text-[#D4FF00]'
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <CalendarBlank size={12} /> Custom
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={align}
          className="w-auto p-3 bg-[#0B0F14] border border-white/10 text-zinc-200"
          data-testid={`${testIdPrefix}-custom-popover`}
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 font-mono">Start date</p>
              <CalendarUI
                mode="single"
                selected={draftFrom}
                onSelect={setDraftFrom}
                disabled={(d) => draftTo && d > draftTo}
                initialFocus
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 font-mono">End date</p>
              <CalendarUI
                mode="single"
                selected={draftTo}
                onSelect={setDraftTo}
                disabled={(d) => draftFrom && d < draftFrom}
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
            <p className="text-[11px] font-mono text-zinc-400">
              {draftFrom ? fmtDDMMYYYY(fmtLocalISO(draftFrom)) : '—'}
              <span className="mx-2 text-zinc-600">→</span>
              {draftTo ? fmtDDMMYYYY(fmtLocalISO(draftTo)) : '—'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPopoverOpen(false)}
                data-testid={`${testIdPrefix}-custom-cancel`}
                className="px-3 py-1.5 text-xs font-mono text-zinc-400 hover:text-zinc-200 transition"
              >Cancel</button>
              <button
                type="button"
                onClick={applyCustom}
                data-testid={`${testIdPrefix}-custom-apply`}
                className="px-3 py-1.5 text-xs font-mono bg-[#D4FF00]/15 text-[#D4FF00] border border-[#D4FF00]/30 rounded hover:bg-[#D4FF00]/25 transition"
              >Apply</button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
